import type { TranslationKey } from '@/i18n-key';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { LocalizedText } from '@/utils/localization';
import type { OptionProps } from '@amelio/ui-library';

import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleEvaluationGroupStatus } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import { getLocalizedText } from '@/utils/localization';

const TYPE_BADGE_ORDER: readonly EvaluationTypeValue[] = [
    EvaluationType.MANAGER_EVALUATION,
    EvaluationType.EVALUATION_360,
    EvaluationType.SELF_EVALUATION,
];

const TYPE_BADGE_LABEL_KEYS: Readonly<Record<EvaluationTypeValue, TranslationKey>> = {
    [EvaluationType.MANAGER_EVALUATION]: 'cycleEvaluationDashboard.typeBadge.managerAssessment',
    [EvaluationType.EVALUATION_360]: 'cycleEvaluationDashboard.typeBadge.feedback360',
    [EvaluationType.SELF_EVALUATION]: 'cycleEvaluationDashboard.typeBadge.selfAssessment',
};

export const getTypeBadgeLabelKey = (type: EvaluationTypeValue): TranslationKey =>
    TYPE_BADGE_LABEL_KEYS[type];

export const appendOptionUnlessValuePresent = (
    options: OptionProps[],
    extra: OptionProps,
): OptionProps[] =>
    options.some((option) => option.value === extra.value) ? options : [...options, extra];

export const getGroupEvaluationTypes = (group: CycleEvaluationGroup): EvaluationTypeValue[] => {
    // Only include active evaluations; dormant evaluations are deselected and should not appear as badges.
    const activeEvaluations = group.cycleEvaluations ?? [];
    const included = new Set(activeEvaluations.map((evaluation) => evaluation.reviewType));

    return TYPE_BADGE_ORDER.filter((type) => included.has(type));
};

export const selectLegacyCycleEvaluations = (
    cycleEvaluations: CycleEvaluation[],
    groups: CycleEvaluationGroup[],
): CycleEvaluation[] => {
    const groupedIds = new Set(
        groups.flatMap((group) =>
            [...(group.cycleEvaluations ?? []), ...(group.dormantCycleEvaluations ?? [])].map(
                (evaluation) => evaluation.id,
            ),
        ),
    );

    return cycleEvaluations.filter((cycleEvaluation) => !groupedIds.has(cycleEvaluation.id));
};

interface SelectGroupSentCyclesEvaluationParams {
    group: CycleEvaluationGroup;
    cycleEvaluations: CycleEvaluation[];
}

export const selectGroupSentCyclesEvaluation = ({
    group,
    cycleEvaluations,
}: SelectGroupSentCyclesEvaluationParams): CycleEvaluation | undefined => {
    const hasLeftDraft = group.status !== CycleEvaluationGroupStatus.DRAFT;

    // Typed as required, but the service casts the server response without runtime validation.
    const feedback360Child = (group.cycleEvaluations ?? []).find(
        ({ reviewType }) => reviewType === EvaluationType.EVALUATION_360,
    );

    return hasLeftDraft && feedback360Child
        ? cycleEvaluations.find(({ id }) => id === feedback360Child.id)
        : undefined;
};

/**
 * The evaluation template (questionnaire) behind one evaluation of a cycle. The cycle name is the
 * identity of a cycle and titles every surface; the template name only answers "which
 * questionnaire" and is shown to administrators under its own label, never as a title.
 */
export interface EvaluationTemplateSummary {
    reviewType: EvaluationTypeValue;
    /** Localized questionnaire title; undefined when the record carries none. */
    title?: LocalizedText;
}

interface SelectGroupEvaluationTemplatesParams {
    group: CycleEvaluationGroup;
    cycleEvaluations: CycleEvaluation[];
}

/**
 * Template titles of the evaluations a cycle includes, in badge order. Group children carry no
 * questionnaire on the wire, so each one is resolved against the full evaluation list.
 */
export const selectGroupEvaluationTemplates = ({
    group,
    cycleEvaluations,
}: SelectGroupEvaluationTemplatesParams): EvaluationTemplateSummary[] => {
    const evaluationsById = new Map(
        cycleEvaluations.map((cycleEvaluation) => [cycleEvaluation.id, cycleEvaluation]),
    );

    return (group.cycleEvaluations ?? [])
        .map((child) => ({
            reviewType: child.reviewType,
            title: evaluationsById.get(child.id)?.cycleQuestionnaire?.title,
        }))
        .sort(
            (a, b) =>
                TYPE_BADGE_ORDER.indexOf(a.reviewType) - TYPE_BADGE_ORDER.indexOf(b.reviewType),
        );
};

/**
 * A cycle created before the redesign has no cycle name: its template title stands in as the
 * title, exactly as before. The service casts the server response without validation, so a record
 * missing its questionnaire degrades to the evaluation's own title instead of throwing.
 */
export const resolveLegacyCycleName = (cycleEvaluation: CycleEvaluation): string =>
    getLocalizedText(cycleEvaluation.cycleQuestionnaire?.title) || cycleEvaluation.title || '';

export const CYCLE_LIST_PAGE_SIZE = 10;

interface PaginateCycleListRowsParams<TGroup, TEvaluation> {
    groups: TGroup[];
    evaluations: TEvaluation[];
    currentPage: number;
    pageSize: number;
}

interface PaginatedCycleListRows<TGroup, TEvaluation> {
    groupsPage: TGroup[];
    evaluationsPage: TEvaluation[];
}

export const paginateCycleListRows = <TGroup, TEvaluation>({
    groups,
    evaluations,
    currentPage,
    pageSize,
}: PaginateCycleListRowsParams<TGroup, TEvaluation>): PaginatedCycleListRows<
    TGroup,
    TEvaluation
> => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
        groupsPage: groups.slice(startIndex, endIndex),
        evaluationsPage: evaluations.slice(
            Math.max(startIndex - groups.length, 0),
            Math.max(endIndex - groups.length, 0),
        ),
    };
};
